import { Injectable, NgZone } from '@angular/core';
import { AuthService } from '../components/framework/services/auth.service';
import { LoggingService } from '../../framework/logging/logging.service';
import { SeverityLevel } from '../../framework/logging/severity-level.model';

/**
 * Service to monitor token validity and proactively handle expiration
 * Checks token status periodically and either renews or logs out user
 */
@Injectable({
  providedIn: 'root'
})
export class TokenMonitorService {
  private monitorIntervalId: any;
  private readonly CHECK_INTERVAL_MS = 60 * 1000; // Check every 60 seconds
  private readonly WARNING_THRESHOLD_MINUTES = 5; // Warn when less than 5 minutes remaining
  private isMonitoring = false;

  constructor(
    private authService: AuthService,
    private loggingService: LoggingService,
    private ngZone: NgZone
  ) {}

  /**
   * Start monitoring token validity
   */
  public startMonitoring(): void {
    if (this.isMonitoring) {
      console.log('🔍 Token monitoring already active');
      return;
    }

    console.log('🚀 Starting token validity monitoring');
    this.isMonitoring = true;

    // Run outside Angular zone to avoid triggering change detection
    this.ngZone.runOutsideAngular(() => {
      this.monitorIntervalId = setInterval(() => {
        this.checkTokenValidity();
      }, this.CHECK_INTERVAL_MS);
    });

    // Do an immediate check
    this.checkTokenValidity();
  }

  /**
   * Stop monitoring token validity
   */
  public stopMonitoring(): void {
    if (this.monitorIntervalId) {
      console.log('🛑 Stopping token validity monitoring');
      clearInterval(this.monitorIntervalId);
      this.monitorIntervalId = null;
      this.isMonitoring = false;
    }
  }

  /**
   * Check token validity and take appropriate action
   */
  private async checkTokenValidity(): Promise<void> {
    try {
      // Check if user is authenticated
      const isAuthenticated = await this.authService.isAuthenticatedAsync();

      if (!isAuthenticated) {
        console.log('🔍 Token monitor: User not authenticated, stopping monitoring');
        this.stopMonitoring();
        return;
      }

      // Check if token is expired or expiring
      const isExpiring = await this.authService.isTokenExpiredOrExpiring();

      if (isExpiring) {
        console.log('⚠️ Token monitor: Token expired or expiring soon');

        // Log the event
        this.loggingService.logError('Token expiry detected by monitor', SeverityLevel.Warning);

        // Try to renew the token
        this.ngZone.run(async () => {
          try {
            console.log('🔄 Token monitor: Attempting automatic renewal');
            const renewedUser = await this.authService.renewToken();

            if (renewedUser) {
              console.log('✅ Token monitor: Token renewed successfully');
              this.loggingService.logError('Token automatically renewed by monitor', SeverityLevel.Information);
            } else {
              console.warn('⚠️ Token monitor: Renewal returned no user');
              this.handleRenewalFailure('No user returned from renewal');
            }
          } catch (error) {
            console.error('❌ Token monitor: Renewal failed', error);
            this.handleRenewalFailure(error);
          }
        });
      } else {
        // Token is valid, check if we should warn user about upcoming expiry
        const authState = this.authService.getAuthenticationState();

        if (authState.timeUntilExpiry) {
          const minutesUntilExpiry = Math.floor(authState.timeUntilExpiry / 1000 / 60);

          if (minutesUntilExpiry <= this.WARNING_THRESHOLD_MINUTES && minutesUntilExpiry > 0) {
            console.log(`⏰ Token monitor: Token expires in ${minutesUntilExpiry} minutes`);

            // Log warning but don't show user notification unless it's very close
            if (minutesUntilExpiry <= 2) {
              this.loggingService.logError(
                `Token expires in ${minutesUntilExpiry} minute(s)`,
                SeverityLevel.Warning
              );
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ Error in token validity check:', error);
      this.loggingService.logError('Token validity check failed', SeverityLevel.Warning);
    }
  }

  /**
   * Handle renewal failure - logout user if token cannot be renewed
   */
  private handleRenewalFailure(error: any): void {
    const errorMessage = error instanceof Error ? error.message : String(error);

    console.error('🚨 Token renewal failed in monitor:', errorMessage);

    // Check if failure is due to cooldown
    if (errorMessage.includes('cooldown')) {
      console.log('⏰ Renewal in cooldown - will retry on next check');
      return;
    }

    // For other failures, log out the user to prevent security issues
    console.log('🚪 Logging out user due to token renewal failure');
    this.loggingService.logError(`User logged out - token expired: ${errorMessage}`, SeverityLevel.Warning);

    this.ngZone.run(() => {
      this.authService.logout();
    });
  }

  /**
   * Force an immediate token validity check
   */
  public checkNow(): void {
    console.log('🔍 Forcing immediate token validity check');
    this.checkTokenValidity();
  }

  /**
   * Get monitoring status
   */
  public getStatus(): { isMonitoring: boolean; checkIntervalMs: number } {
    return {
      isMonitoring: this.isMonitoring,
      checkIntervalMs: this.CHECK_INTERVAL_MS
    };
  }
}
